// helpdesk-frontend/src/utils/motywAplikacji.js
/**
 * [polecenie właściciela 2026-10-03] DOMYŚLNY WYGLĄD APLIKACJI JEST JASNY.
 * „ustaw, że kolor domyślny aplikacji to biały, nie ciemny, user to potem sobie zmieni”.
 *
 * Jedno miejsce, które mówi, JAK zapamiętujemy motyw. Korzystają z niego cztery strony:
 *  - `context/ThemeContext.js` — ustawia `html[data-theme]` i kolor paska przeglądarki,
 *  - `context/PersonalizationContext.js` — zapisuje wybór NA KONCIE razem z kolorem akcentu i czcionką
 *    (PUT /api/user-preferences podmienia CAŁY zestaw, więc wszystkie klucze konta jadą jednym zapisem),
 *  - `context/BrandingContext.js` — kolor marki w pasku przeglądarki (tylko prawdziwy kolor, nie czerń/biel),
 *  - skrypt startowy w `public/index.html` — czyta TEN SAM klucz, zanim wstanie React (bez mignięcia).
 *
 * BYŁO ŹLE: ThemeContext zapisywał `app-theme` przy KAŻDYM montażu, także przy pierwszym wejściu.
 * Wartość domyślna lądowała więc w przeglądarce tak, jakby ktoś ją wybrał. Między 8.12.2025 a 1.03.2026
 * domyślny był ciemny, więc przeglądarki z tamtego okresu startowały ciemno „z wyboru”, którego nikt nie zrobił,
 * i nie dało się odróżnić takiego zapisu od świadomego kliknięcia.
 *
 * TERAZ: zapis powstaje WYŁĄCZNIE z kliknięcia (przełącznik w nagłówku i na logowaniu, menu „Dostępność
 * i personalizacja”, okno „Wygląd aplikacji”) i leży pod NOWYM kluczem. Brak zapisu = jasny.
 * Stary klucz jest ignorowany i sprzątany — świadomie także u tych, którzy kiedyś wybrali ciemny, bo ich
 * wyboru nie da się odróżnić od zapisu automatycznego. Okno „Wygląd aplikacji” mówi im, jak wrócić.
 * Ustawienie systemu (`prefers-color-scheme`) niczego nie przełącza.
 */
import { createContext } from "react";

/** Jawny wybór motywu w tej przeglądarce: "light" | "dark". Brak klucza = domyślny (jasny). */
export const KLUCZ_MOTYWU = "app-motyw";
/**
 * CZYJ i JAKI to wybór (poprawka po przeglądzie 2026-10-04). Trzy stany, bo każdy rozstrzyga się inaczej przy
 * wczytaniu konta, które NIE MA motywu:
 *  - `"<id>"`            — zgodny z kontem <id> (serwer potwierdził). Konto bez motywu znaczy wtedy, że wybór zdjęto
 *                          na innym urządzeniu („Przywróć domyślne”) → wygrywa konto, czyli jasny. BYŁO ŹLE: taki zapis
 *                          wracał na konto automatycznym PUT i ciemny „zmartwychwstawał” na wszystkich urządzeniach;
 *  - `"<id>:oczekuje"`   — kliknięty na koncie <id>, zapis na serwerze jeszcze niepotwierdzony → jedzie na konto;
 *  - `"logowanie:<ms>"`  — kliknięty PRZED zalogowaniem (księżyc na ekranie logowania) w chwili <ms>. Świeży
 *                          (do 15 minut) wygrywa nawet z motywem konta i trafia na konto — to najnowsza decyzja osoby,
 *                          która właśnie się loguje. Starszy jest porzucany: na wspólnym komputerze ktoś mógł kliknąć
 *                          i odejść, a jego wybór nie może trafić na konto następnej osoby.
 */
export const KLUCZ_WLASCICIELA_MOTYWU = "app-motyw-konto";
/** Klucz sprzed 3.10.2026 — zapisywany automatycznie, więc nic nie znaczy. Ignorowany i sprzątany. */
export const KLUCZ_MOTYWU_SPRZED_2026_10 = "app-theme";

export const MOTYW_JASNY = "light";
export const MOTYW_CIEMNY = "dark";
export const MOTYWY = [MOTYW_JASNY, MOTYW_CIEMNY];
export const MOTYW_DOMYSLNY = MOTYW_JASNY;

/** Jak długo wybór z ekranu logowania jest „świeży” (należy do osoby, która właśnie się loguje). */
export const WAZNOSC_WYBORU_Z_LOGOWANIA_MS = 15 * 60 * 1000;

const PRZYROSTEK_OCZEKUJE = ":oczekuje";
const PRZEDROSTEK_LOGOWANIA = "logowanie:";

export const wlascicielZgodny = (idKonta) => String(idKonta);
export const wlascicielOczekujacy = (idKonta) => `${idKonta}${PRZYROSTEK_OCZEKUJE}`;
export const wlascicielLogowania = (teraz = Date.now()) => `${PRZEDROSTEK_LOGOWANIA}${teraz}`;

export const czyMotyw = (wartosc) => MOTYWY.includes(wartosc);

/** Jawny wybór zapisany w tej przeglądarce albo `null` (nikt nie wybierał). */
export const czytajWyborMotywu = () => {
    try {
        const v = window.localStorage.getItem(KLUCZ_MOTYWU);
        return czyMotyw(v) ? v : null;
    } catch {
        return null;
    }
};

export const czytajWlascicielaMotywu = () => {
    try {
        return window.localStorage.getItem(KLUCZ_WLASCICIELA_MOTYWU);
    } catch {
        return null;
    }
};

/**
 * Zapis jawnego wyboru. `null` kasuje wybór (powrót do domyślnego jasnego).
 * `wlasciciel` — jeden z trzech stanów wyżej; domyślnie „kliknięty przed zalogowaniem, teraz”.
 */
export const zapiszWyborMotywu = (motyw, wlasciciel = wlascicielLogowania()) => {
    try {
        if (czyMotyw(motyw)) {
            window.localStorage.setItem(KLUCZ_MOTYWU, motyw);
            if (wlasciciel) window.localStorage.setItem(KLUCZ_WLASCICIELA_MOTYWU, String(wlasciciel));
            else window.localStorage.removeItem(KLUCZ_WLASCICIELA_MOTYWU);
        } else {
            window.localStorage.removeItem(KLUCZ_MOTYWU);
            window.localStorage.removeItem(KLUCZ_WLASCICIELA_MOTYWU);
        }
    } catch {
        /* brak pamięci przeglądarki = wybór żyje do odświeżenia (i na koncie, jeśli jest zalogowany) */
    }
};

/** Serwer potwierdził zapis — wybór staje się „zgodny z kontem”. */
export const zapiszWlascicielaMotywu = (wlasciciel) => {
    try {
        window.localStorage.setItem(KLUCZ_WLASCICIELA_MOTYWU, String(wlasciciel));
    } catch {
        /* quota */
    }
};

const czasWyboruZLogowania = (wlasciciel) => {
    if (typeof wlasciciel !== "string" || !wlasciciel.startsWith(PRZEDROSTEK_LOGOWANIA)) return null;
    const ms = Number(wlasciciel.slice(PRZEDROSTEK_LOGOWANIA.length));
    return Number.isFinite(ms) ? ms : null;
};

/** Kliknięty na ekranie logowania nie dawniej niż 15 minut temu (zegar z przyszłości = nie). */
const swiezyZLogowania = (wlasciciel, teraz) => {
    const czasLogowania = czasWyboruZLogowania(wlasciciel);
    return czasLogowania != null
        && teraz - czasLogowania >= 0
        && teraz - czasLogowania <= WAZNOSC_WYBORU_Z_LOGOWANIA_MS;
};

/**
 * ROZSTRZYGNIĘCIE przy wczytaniu konta (czysta funkcja — zapadka 1003 sprawdza każdy stan osobno).
 * @returns {{ motyw: "light"|"dark"|null, zrodlo: "logowanie"|"konto"|"oczekujacy"|"brak" }}
 *   `motyw` null = brak wyboru (jasny). Źródło „logowanie” i „oczekujacy” znaczy: wybór jedzie na konto.
 */
export const rozstrzygnijMotywKonta = ({ motywKonta, lokalny, wlasciciel, idKonta, teraz = Date.now() }) => {
    if (czyMotyw(lokalny) && swiezyZLogowania(wlasciciel, teraz)) return { motyw: lokalny, zrodlo: "logowanie" };
    if (czyMotyw(motywKonta)) return { motyw: motywKonta, zrodlo: "konto" };
    if (czyMotyw(lokalny) && idKonta != null && wlasciciel === wlascicielOczekujacy(idKonta)) {
        return { motyw: lokalny, zrodlo: "oczekujacy" };
    }
    return { motyw: null, zrodlo: "brak" };
};

/* ── EKRAN BEZ KONTA I WEJŚCIE NA KONTO (domknięcie po krytyku 2026-10-04) ───────────────────────────────────────────
   BYŁO ŹLE: na wspólnym komputerze ekran logowania po wylogowaniu pokazywał motyw POPRZEDNIEJ osoby (pamięć przeglądarki
   zostaje), a po zalogowaniu następnej osoby ekran migał ciemny → jasny, gdy przyszło jej konto.
   TERAZ: wybór należy do konta, więc na ekranie bez konta (logowanie, strony publiczne) stoi domyślny jasny — chyba że
   ktoś przed chwilą kliknął księżyc właśnie na tym ekranie. Pamięć przeglądarki NIE jest kasowana: ta sama osoba po
   zalogowaniu dostaje swój wybór od razu (bez czekania na serwer), a inna osoba — nie. */

/** Klucz, pod którym AuthContext trzyma dane zalogowanego konta (zdejmowany przy wylogowaniu). Brak = ekran bez konta. */
export const KLUCZ_SESJI_W_PRZEGLADARCE = "user";

export const czySesjaWPrzegladarce = () => {
    try {
        return !!window.localStorage.getItem(KLUCZ_SESJI_W_PRZEGLADARCE);
    } catch {
        return false;
    }
};

/** Ekran BEZ konta: tylko świeże kliknięcie z ekranu logowania, inaczej `null` (jasny). */
export const motywEkranuBezKonta = ({ lokalny, wlasciciel, teraz = Date.now() }) =>
    (czyMotyw(lokalny) && swiezyZLogowania(wlasciciel, teraz) ? lokalny : null);

/** Konto właśnie weszło: jego własny wybór z tej przeglądarki (zgodny albo oczekujący) albo świeże kliknięcie z logowania.
    Wybór innej osoby — `null` (nic nie pokazujemy, rozstrzygnie odczyt konta). */
export const motywTegoKonta = ({ lokalny, wlasciciel, idKonta, teraz = Date.now() }) => {
    if (!czyMotyw(lokalny) || idKonta == null) return null;
    if (wlasciciel === wlascicielZgodny(idKonta) || wlasciciel === wlascicielOczekujacy(idKonta)) return lokalny;
    return swiezyZLogowania(wlasciciel, teraz) ? lokalny : null;
};

/**
 * Pierwszy kadr Reacta — TA SAMA reguła co skrypt `#motyw-startowy` w public/index.html (zapadka porównuje oba):
 * jest sesja (F5 zalogowanego, także w podglądzie konta) → zapisany wybór; brak sesji → jak na ekranie bez konta.
 */
export const motywStartowy = (teraz = Date.now()) => {
    const lokalny = czytajWyborMotywu();
    if (czySesjaWPrzegladarce()) return lokalny;
    return motywEkranuBezKonta({ lokalny, wlasciciel: czytajWlascicielaMotywu(), teraz });
};

export const sprzatnijZapisMotywuSprzed2026_10 = () => {
    try {
        window.localStorage.removeItem(KLUCZ_MOTYWU_SPRZED_2026_10);
    } catch {
        /* nic do sprzątania */
    }
};

/* ── PASEK PRZEGLĄDARKI (meta theme-color) ─────────────────────────────────────────────────────────────────
   [poprawka po przeglądzie 2026-10-04] Na telefonie pasek przeglądarki był CZARNY nad białą aplikacją: index.html
   miał domyślne #000000 z szablonu CRA, a branding dokładał swoje #000000. Teraz pasek idzie za motywem (kolor tła
   nagłówka), a kolor marki z panelu nadpisuje go tylko wtedy, gdy to prawdziwy kolor — BrandingContext oznacza wtedy
   znacznik atrybutem i ten moduł go nie rusza. Te same wartości stoją w skrypcie #motyw-startowy w index.html. */
export const KOLOR_PASKA_PRZEGLADARKI = { light: "#ffffff", dark: "#17181b" };
export const ATRYBUT_KOLORU_MARKI = "data-kolor-marki";

export const ustawKolorPaskaPrzegladarki = (motyw) => {
    try {
        const meta = document.head.querySelector('meta[name="theme-color"]');
        if (!meta || meta.hasAttribute(ATRYBUT_KOLORU_MARKI)) return;
        meta.setAttribute("content", motyw === MOTYW_CIEMNY ? KOLOR_PASKA_PRZEGLADARKI.dark : KOLOR_PASKA_PRZEGLADARKI.light);
    } catch {
        /* brak dokumentu (test bez DOM) */
    }
};

/**
 * KANAŁ MOTYWU KONTA — nie jest magazynem stanu, tylko łączem między dwoma kontekstami.
 * `PersonalizationProvider` (wyżej w drzewie, zna konto i jest jedynym pisarzem preferencji na serwer)
 * wystawia tu `{ motyw, ustawMotyw }`. `ThemeProvider` (niżej, w App.js) czyta z kanału, a gdy kanału nie ma
 * (test bez providerów, strona awarii) — działa samodzielnie na pamięci przeglądarki.
 * Osobny plik bez zależności, żeby ThemeContext nie ciągnął za sobą AuthContext i warstwy HTTP.
 */
export const KanalMotywuKonta = createContext(null);
